// Assistant sidebar: sections, conversation rows, agent glyphs
// (WEB-8005). Composed by praxisStyles.ts at the section's original
// position — source order is part of the cascade.
export const praxisSidebar = `
/* Sidebar */
.pr-sidebar {
  background: var(--pr-surface);
  border-right: 1px solid var(--pr-hairline);
}
.pr-sidebar-new {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px;
  border-radius: var(--pr-r-sm);
  background: transparent; border: none;
  cursor: pointer;
  font-size: 13px; font-weight: 500;
  color: var(--pr-ink);
  transition: background 120ms ease, color 120ms ease;
  width: 100%;
  text-align: left;
  font-family: inherit;
}
.pr-sidebar-new:hover { background: var(--pr-surface-2); color: var(--pr-accent-ink); }
.pr-sidebar-new .pr-shortcut {
  margin-left: auto;
  font-family: var(--pr-font-mono); font-size: 11px;
  color: var(--pr-ink-4);
}

.pr-sb-section-head {
  width: 100%;
  display: flex; align-items: center; gap: 6px;
  padding: 10px 14px 6px 18px;
  font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--pr-ink-4);
  text-transform: uppercase;
  cursor: pointer;
  background: none; border: none;
  text-align: left;
  font-family: inherit;
  transition: color 120ms ease;
}
.pr-sb-section-head:hover { color: var(--pr-ink-3); }
.pr-sb-section-head.plain { cursor: default; }
/* Action variant used for "New chat" — same muted section-head typography
   but with a subtle hover background so it reads as a clickable action. */
.pr-sb-section-head.pr-sb-section-head--action:hover {
  background: var(--pr-surface-2);
  color: var(--pr-accent-ink);
}
/* The SquarePen glyph's optical center sits a touch above the uppercase
   text baseline. Nudge the label down so they read as horizontally aligned. */
.pr-sb-section-head.pr-sb-section-head--action > span { margin-top: 2px; }
.pr-sb-section-head .pr-chev { display: inline-flex; transition: transform 140ms ease; }
.pr-sb-section-head .pr-chev.open { transform: rotate(90deg); }

.pr-conv {
  display: flex; align-items: center; gap: 6px;
  padding: 0 14px 0 18px;
  height: 32px;
  cursor: pointer;
  color: var(--pr-ink-2);
  transition: background 80ms ease, color 80ms ease;
  position: relative;
  text-align: left;
  background: none; border: none;
  width: 100%;
  font-family: inherit;
}
.pr-conv:hover { background: var(--pr-accent-soft); }
.pr-conv.active { background: var(--pr-accent-soft); }
.pr-conv.editing,
.pr-conv.editing:hover { background: transparent; cursor: default; }
.pr-conv-icon {
  width: 11px; height: 11px;
  display: grid; place-items: center;
  color: var(--pr-ink-3);
  flex-shrink: 0;
}
.pr-conv-icon svg { width: 11px; height: 11px; stroke-width: 1.7; }
/* Date divider between messages — sticks to the top of the scroll area so
   the user always sees which day the visible messages belong to, until the
   next day's divider scrolls into view and covers it. */
.pr-date-divider {
  position: sticky;
  top: 0;
  z-index: 25;
  padding: 8px 0 6px;
}
/* Background extends full width of the scroll pane (well past the message
   column) so tooltips / focus rings can't bleed past the sticky bar at its
   edges. The element itself still only sizes to the content column. */
.pr-date-divider::before {
  content: '';
  position: absolute;
  inset: 0;
  left: -100vw;
  right: -100vw;
  background: var(--pr-surface);
  z-index: -1;
}

/* Shared sizing for row-level icons (run section headers, sched/hook headers)
   so they render identically to the sidebar conversation icons. */
.pr-row-icon {
  width: 14px; height: 14px;
  display: grid; place-items: center;
  flex-shrink: 0;
}
.pr-row-icon svg { width: 14px; height: 14px; stroke-width: 1.7; }
.pr-conv.active .pr-conv-icon { color: var(--pr-accent); }
.pr-conv-icon.unread { color: var(--pr-danger); }
.pr-conv-icon.unread svg { stroke-width: 2.5; }
.pr-unread-dot {
  display: block;
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--pr-danger);
}
/* Lucide icons in the sidebar use a lighter stroke to match the design */
.pr-sb-section-head svg,
.pr-sidebar-new svg { width: 14px; height: 14px; stroke-width: 1.7; }
.pr-sb-section-head .pr-chev svg { width: 11px; height: 11px; }
.pr-conv-text { min-width: 0; flex: 1; }
.pr-conv-title {
  font-size: 13px; color: var(--pr-ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-weight: 400;
}
.pr-conv.active .pr-conv-title { font-weight: 500; }
.pr-conv-meta {
  font-size: 11px; color: var(--pr-ink-4); margin-top: 1px;
  font-variant-numeric: tabular-nums;
  display: flex; align-items: center; gap: 8px;
}
.pr-conv-meta .pr-sched-tag { color: var(--pr-accent); font-weight: 500; }
.pr-conv-actions {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex; gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 80ms ease;
}
.pr-conv:hover .pr-conv-actions { opacity: 1; pointer-events: auto; }
/* Reserve space for actions on hover so the title ellipses cleanly
   instead of running under the buttons. */
.pr-conv:hover .pr-conv-text { padding-right: 52px; }
.pr-conv.editing .pr-conv-text,
.pr-conv.editing:hover .pr-conv-text { padding-right: 0; }
.pr-conv-actions .pr-iconbtn { width: 20px; height: 20px; }
.pr-conv-actions .pr-iconbtn svg { width: 12px; height: 12px; }
.pr-conv-actions .pr-iconbtn:hover { background: var(--pr-surface-2); color: var(--pr-ink); }
.pr-conv-actions .pr-iconbtn.pr-iconbtn-danger:hover { background: var(--pr-danger-soft); color: var(--pr-danger); }
.pr-iconbtn.pr-iconbtn-confirm,
.pr-iconbtn.pr-iconbtn-confirm:hover { background: var(--pr-danger); color: white; }
.pr-conv-edit {
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--pr-accent);
  border-radius: 0;
  padding: 0 0 1px;
  font-size: 13px;
  color: var(--pr-ink);
  outline: none;
  width: 100%;
  font-family: inherit;
}
.pr-sidebar-error {
  margin: 6px 14px 0;
  padding: 8px 10px;
  border-radius: var(--pr-r-sm);
  background: var(--pr-danger-soft);
  color: oklch(0.38 0.16 25);
  font-size: 12px;
}

.pr-unread-pill {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; padding: 0 5px;
  border-radius: 9px;
  background: oklch(0.58 0.18 25);
  color: white;
  font-size: 10px; font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-left: auto;
}
.pr-unread-pill.sm {
  min-width: 14px; height: 14px;
  font-size: 9.5px;
  padding: 0 4px;
}
.pr-unread-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: oklch(0.58 0.18 25);
  flex-shrink: 0;
}
.pr-active-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--pr-success);
  flex-shrink: 0;
}
/* Hide unread dot when conv row actions are showing */
.pr-conv:hover .pr-unread-dot,
.pr-conv:hover .pr-conv-unread { display: none; }

/* Agent glyphs (collapsed sidebar) — borderless, coloured icon only */
.pr-agent-glyph {
  position: relative;
  width: 30px; height: 30px;
  display: grid; place-items: center;
  border: none;
  border-radius: var(--pr-r-sm);
  background: transparent;
  cursor: pointer;
  transition: background 120ms ease, opacity 120ms ease;
  padding: 0;
}
.pr-agent-glyph:hover { background: var(--pr-surface-2); }
.pr-agent-glyph.active { background: var(--pr-accent-soft); }
.pr-agent-glyph svg { width: 18px; height: 18px; stroke-width: 1.6; }
.pr-agent-chip-dot {
  position: absolute;
  top: 3px; right: 3px;
  width: 6px; height: 6px;
  background: oklch(0.58 0.18 25);
  border-radius: 50%;
  border: 1px solid var(--pr-surface);
}
`;
